/* eslint-disable react/no-danger */
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Button, Checkbox, Group, Modal, Stack } from '@mantine/core';

import { removeTags } from 'src/lib/textUtils';
import RadioGroup from 'src/components/UI/RadioGroup/RadioGroup';
import useDelayedOpening from 'src/new_arch/hooks/useDelayedOpening';
import useStackeableModal from 'src/new_arch/hooks/useStackeableModal';
import useCloseOnEscape from 'src/new_arch/hooks/useCloseOnEscape';

import './Popup.scss';

import Text from '../Text/Text';
import Icon from '../Icon/Icon';
import { PopupProps } from './types';

const DEFAULT_STYLES = {
	content: {
		backgroundColor: 'var(--color-bg-container)'
	},
	header: {
		padding: '10px 16px',
		borderBottom: '1px solid var(--color-outline-default)',
		backgroundColor: 'var(--color-bg-container)'
	},
	title: {
		color: 'var(--color-text-primary)'
	},
	close: {
		color: 'var(--color-text-primary)'
	},
	body: {
		padding: 0
	}
};

const FOOTER_STYLES = {
	padding: '6px 12px',
	borderTop: '1px solid var(--color-outline-default)'
};

const Popup = ( {
	title,
	description,
	descriptionAlignment = 'left',
	checkboxDescription,
	actions,
	onCrossClick,
	radioOptions,
	onRadioOptionSelect,
	renderCustomPopup,
	name,
	headerIcon,
	headerIconColor = 'blue',
	stackOrder = 2
}: PopupProps ) => {
	const hasCheckbox = !!checkboxDescription;
	const hasRadioOptions = radioOptions && radioOptions.length > 0;
	const opened = useDelayedOpening();
	const { modalStyles } = useStackeableModal( { stackOrder, defaultStyles: DEFAULT_STYLES } );

	const [ checked, setChecked ] = useState( false );

	const parseBoldTags = ( aDescription: string ) => (
		removeTags( aDescription )
			.replaceAll( '*startBold*', '<span>' )
			.replaceAll( '*endBold*', '</span>' )
			.replaceAll( '*jumpLine*', '<br />' )
	);

	const history = useHistory();

	useCloseOnEscape( { onClose: onCrossClick } );

	useEffect( () => {
		if ( !name ) return () => {};

		const { pathname, state, search } = history.location;
		const queryParams = new URLSearchParams( search );
		queryParams.append( 'modal', name );
		history.replace( { pathname, state, search: queryParams.toString() } );

		return () => history.replace( { pathname, state, search } );
	}, [ name ] );

	return renderCustomPopup ? (
		<div className="Popup">
			{renderCustomPopup()}
		</div>
	) : (
		<Modal
			opened={opened}
			onClose={onCrossClick}
			title={headerIcon ? (
				<Group spacing={8}>
					<Icon icon={headerIcon} color={headerIconColor} />
					{title}
				</Group>
			) : title}
			centered
			styles={modalStyles}
			size={444}
		>
			<Stack p="24px">
				{description && (
					<Text align={descriptionAlignment}>
						<p dangerouslySetInnerHTML={{ __html: parseBoldTags( description ) }} />
					</Text>
				)}
				{hasCheckbox && (
					<Checkbox
						label={checkboxDescription}
						size="xs"
						onChange={event => setChecked( event.target.checked )}
						checked={checked}
					/>
				)}
				{hasRadioOptions
					&& <RadioGroup options={radioOptions} onOptionSelect={onRadioOptionSelect} />}
			</Stack>
			<Group style={FOOTER_STYLES} position="right" spacing={12}>
				{actions
					.map( ( {
						text, color, onClick, enabledByCheckbox, variant
					} ) => (
						<Button
							key={text}
							variant={variant}
							color={color}
							onClick={onClick}
							disabled={enabledByCheckbox && !checked}
						>
							{text}
						</Button>
					) )}
			</Group>
		</Modal>
	);
};

export default Popup;
